export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type AnalyticsEventData = Record<string, JsonValue>;

type UmamiTracker = { track: (payload: Record<string, unknown>) => Promise<unknown> | void };
declare global { interface Window { umami?: UmamiTracker } }

export const UMAMI_WEBSITE_ID = 'd3f43c8c-958f-4e5a-9d78-29cd66da377d';
export const UMAMI_SCRIPT_URL = 'https://analytics.waedo.nl/script.js';
const SENSITIVE_KEYS = /(?:password|secret|token|authorization|email|phone|name|code|player.?id|evaluation|query)/i;
const MAX_PENDING = 100;

/** Strip query/hash and redact route parameters that can contain private IDs or access keys. */
export const analyticsPath = (value: string): string => {
  const path = value.split(/[?#]/, 1)[0];
  return path
    .replace(/(\/(?:intake|share|team|teams|team-management|club|clubs|player|players|match|matches|plans|sessions|training-sessions|training-practices|exercise|exercises)\/)([^/]+)/gi, (_, prefix: string, segment: string) => `${prefix}${['new', 'edit', 'live', 'editor'].includes(segment) ? segment : ':detail'}`)
    .replace(/\/[^/]*[0-9][^/]*(?=\/|$)/g, '/:detail')
    .slice(0, 500) || '/';
};

const sanitizeValue = (value: unknown, depth = 0): JsonValue | undefined => {
  if (depth > 3 || value === undefined || typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.slice(0, 500);
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (Array.isArray(value)) return value.slice(0, 50).map(item => sanitizeValue(item, depth + 1)).filter((item): item is JsonValue => item !== undefined);
  if (typeof value === 'object') {
    const result: Record<string, JsonValue> = {};
    for (const [key, item] of Object.entries(value).slice(0, 40)) {
      if (SENSITIVE_KEYS.test(key) || /^(?:url|href|referrer|referer|title|id)$/i.test(key)) continue;
      const safe = key === 'path' && typeof item === 'string' ? analyticsPath(item) : sanitizeValue(item, depth + 1);
      if (safe !== undefined) result[key] = safe;
    }
    return result;
  }
  return undefined;
};

const sanitizeProperties = (properties?: Record<string, unknown>): AnalyticsEventData => {
  const safe = sanitizeValue(properties);
  return safe && typeof safe === 'object' && !Array.isArray(safe) ? safe : {};
};

export const analyticsArea = (app: string, hostname: string, path: string): string => {
  if (/^live[.-]/.test(hostname) || /^\/(?:[a-z]{2}\/)?live(?:\/|$)/.test(path)) return 'live';
  if (/^trainer[.-]/.test(hostname) || /^\/(?:[a-z]{2}\/)?(?:trainer(?:-dugout)?|training-[a-z-]+)(?:\/|$)/.test(path)) return 'trainer';
  return app;
};

type AnalyticsOptions = {
  app: 'portal' | 'site' | 'mobile';
  versionPath: string;
  enabled: boolean;
  websiteId?: string;
  scriptUrl?: string;
};

export const createAnalytics = (options: AnalyticsOptions) => {
  const websiteId = options.websiteId ?? UMAMI_WEBSITE_ID;
  const scriptUrl = options.scriptUrl ?? UMAMI_SCRIPT_URL;
  let ready: Promise<{ tracker: UmamiTracker; version: string } | null> | undefined;
  let pending = 0;
  let lastPagePath: string | undefined;
  let profileId: string | undefined;
  let profileProperties: AnalyticsEventData = {};

  const allowed = () => options.enabled && !!websiteId && !!scriptUrl && typeof window !== 'undefined'
    && !['localhost', '127.0.0.1', '[::1]', '::1'].includes(window.location.hostname);

  const loadTracker = (): Promise<UmamiTracker | null> => new Promise(resolve => {
    if (window.umami) { resolve(window.umami); return; }
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.defer = true;
    script.dataset.websiteId = websiteId;
    // Manual payloads work on the deployed v2 tracker and newer Umami versions.
    script.dataset.autoTrack = 'false';
    script.dataset.excludeSearch = 'true';
    script.dataset.excludeHash = 'true';
    const timer = window.setTimeout(() => finish(null), 10000);
    const finish = (tracker: UmamiTracker | null) => {
      window.clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
      resolve(tracker);
    };
    script.onload = () => finish(window.umami ?? null);
    script.onerror = () => finish(null);
    document.head.appendChild(script);
  });

  const getReady = () => {
    if (!ready) {
      ready = Promise.all([
        loadTracker(),
        Promise.resolve().then(() => fetch(options.versionPath, { cache: 'no-store', signal: AbortSignal.timeout(10000) }))
          .then(response => response.ok ? response.json() as Promise<{ version?: unknown }> : null)
          .then(data => typeof data?.version === 'string' && data.version.trim() ? data.version.trim() : null),
      ]).then(([tracker, version]) => tracker && version ? { tracker, version } : null).catch(() => null);
    }
    return ready;
  };

  const send = (name: string | undefined, page: string | undefined, properties?: Record<string, unknown>) => {
    if (!allowed() || pending >= MAX_PENDING) return;
    const hostname = window.location.hostname;
    const path = analyticsPath(window.location.pathname);
    const area = analyticsArea(options.app, hostname, path);
    let referrer = '';
    try { referrer = document.referrer ? new URL(document.referrer).origin : ''; } catch { /* Ignore invalid referrers. */ }
    const data = { ...profileProperties, ...sanitizeProperties(properties), ...(page ? { page } : {}), app: options.app, area };
    const payload = {
      website: websiteId, hostname, url: path, title: page ?? area, referrer,
      language: navigator.language, screen: `${window.screen.width}x${window.screen.height}`,
      tag: area, ...(name ? { name } : {}), ...(profileId ? { id: profileId } : {}),
    };
    pending += 1;
    // Capture route and identity before waiting so early events keep their original context.
    void getReady().then(async result => {
      if (result) await result.tracker.track({ ...payload, data: { ...data, app_version: result.version } });
    }).catch(() => { /* Analytics must never affect application behavior. */ }).finally(() => { pending -= 1; });
  };

  return {
    trackEvent: (eventName: string, properties?: Record<string, unknown>): void => {
      const name = eventName.trim().slice(0, 50);
      if (name) send(name, undefined, properties);
    },
    trackPageView: (pageName: string, properties?: Record<string, unknown>): void => {
      if (!allowed()) return;
      // One view per consecutive pathname; StrictMode and existing page hooks may both call this.
      const path = window.location.pathname;
      if (lastPagePath === path) return;
      lastPagePath = path;
      send(undefined, pageName, properties);
    },
    identifyUser: (id: string, properties?: Record<string, unknown>): void => {
      profileId = id.trim() || undefined;
      profileProperties = sanitizeProperties(properties);
    },
    clearUser: (): void => { profileId = undefined; profileProperties = {}; },
  };
};
